import { emitGAEvent } from '../../utils/ga-event-helpers';
import { isFeatureOn, isGrowthbookReady, registerTrackedExperiment } from './growthbook';

// TPD-587: last-minute hotel CPA experiment, Booking run. For eligible traffic
// that would send the front tab to Kayak (CPC), the treatment arm swaps the
// front partner to the CPA partner of the current run.
// Eligibility lives entirely in this code — the GrowthBook targeting stays
// empty (bar the client=front owner-scoping attribute) so exposure === eligible.
//
// The CPA partner has moved twice: Booking (initial build) → Expedia (TPD-295,
// live 2026-07-14..08-02) → Booking (TPD-587). Product runs these SEQUENTIALLY,
// each as its own GrowthBook experiment with its own control — never
// concurrently — so the feature key moves together with the partner:
//   * the GrowthBook tracking key MUST equal the feature key or exposures are
//     silently dropped as untracked (see growthbook.ts);
//   * reusing one key across runs would merge two experiments into a single
//     experiment_id in viewed_experiment.
// Switching the CPA partner therefore means the two constants below, nothing else.

export const LAST_MINUTE_HOTEL_FEATURE_KEY = 'last-minute-hotel-bookings-com';

registerTrackedExperiment(LAST_MINUTE_HOTEL_FEATURE_KEY);

export const KAYAK_FRONT_PARTNER = 'k';
export const BOOKING_FRONT_PARTNER = 'b';
export const EXPEDIA_FRONT_PARTNER = 'ex';

// The CPA arm of the current run.
export const TREATMENT_FRONT_PARTNER = BOOKING_FRONT_PARTNER;

const ELIGIBLE_COUNTRIES = ['US', 'CA'];
const MIN_LOS = 4;
// Ticket (TPD-587, same wording as the TPD-295 rev. of 2026-07-05): "If the URL
// contains pt=2, ignore it, using the CPA model" — pt=2 does not exclude a user;
// in the treatment arm the pt=2 param is dropped from the FRONT-tab redirect so
// the standard CPA flow applies. Control keeps its URL (and pt) untouched.
const IGNORED_PT = '2';

export interface LastMinuteHotelSignals {
	countryCode?: string | null;
	hasGclid: boolean;
	los?: number | string;
	f?: string;
}

const firstValue = (value: unknown): string | undefined => {
	if (Array.isArray(value)) return value[0];
	return value === undefined || value === null ? undefined : String(value);
};

// The Google-Ads signal must be the gclid present when the visitor
// ARRIVES ON THE HOTELS VERTICAL — not the live URL at search-submit. Re-reading
// the live URL at submit was measured wrong in BOTH directions on prod
// (2026-07-15): navigation inside /hotels drops the gclid, so ~42% of genuine
// AdWords US hotel searchers (backend-attributed, gclid in
// impression_event.traffic_click_id) read false and never entered the experiment
// (under-inclusion); a stray gclid on a non-Google visit read true (a Bing
// visitor was wrongly exposed). Fix: snapshot the signal ONCE, on the FIRST
// hotels-page init(), while the URL still carries the landing gclid.
//
// The snapshot is scoped to a HOTELS landing on purpose. The experiment targets
// people the Google ad brought to the hotels vertical; AdWords lands
// vertical-specifically (measured: hotels/flights/cars, no homepage), so a
// visitor who arrived via a flights/cars ad and only later wandered into a hotel
// search is NOT an intended participant. Gating the capture on the hotels landing
// keeps them out while still recovering every real hotels-ad arrival (all
// observed under-included users landed on hotels).
let landingSignalCaptured = false;
let landingHasGoogleAdsClick = false;

// Call from init(vert) (client-only) BEFORE any navigation, while
// window.location.search still holds the landing URL that init() forwards to the
// backend /init request. Only the FIRST hotels-vertical init counts: non-hotels
// inits are ignored (do not latch), so the snapshot reflects the hotels arrival;
// once latched, a later /hotels load that lost the param cannot erase a real
// arrival gclid, and a stray gclid appearing mid-session cannot flip it on.
// Fail-open: on any error the signal stays false (visitor stays out).
export const captureLandingGoogleAdsSignal = (vert?: string): void => {
	try {
		if (landingSignalCaptured || typeof window === 'undefined') return;
		if (vert !== 'hotels') return; // only a hotels arrival defines this experiment's Google-Ads signal
		landingSignalCaptured = true;
		landingHasGoogleAdsClick = Boolean(new URLSearchParams(window.location.search).get('gclid'));
	} catch (e) {
		console.log('last-minute-hotel: landing gclid capture failed', e);
	}
};

// Pure getter over the landing-captured signal; never throws.
const hasLandingGoogleAdsClick = (): boolean => landingHasGoogleAdsClick;

// Everything except geo: Google Ads traffic (gclid captured on the landing
// request — the same signal the backend uses for trafficSource attribution),
// LOS >= 4, front tab bound for Kayak via a live-URL f=k. Split out so submits
// that arrive before /init resolves (country still unknown) can be counted
// instead of vanishing from monitoring.
const passesNonGeoCriteria = (signals: LastMinuteHotelSignals): boolean =>
	signals.hasGclid &&
	Number(signals.los) >= MIN_LOS &&
	signals.f === KAYAK_FRONT_PARTNER;

// Ticket-description criteria only (comments are out of scope). Anything
// missing or off means "not a participant" — no exposure, no treatment.
export const isLastMinuteHotelEligible = (signals: LastMinuteHotelSignals): boolean =>
	Boolean(signals.countryCode) &&
	ELIGIBLE_COUNTRIES.includes(signals.countryCode as string) &&
	passesNonGeoCriteria(signals);

type PassthruFormData = Record<string, unknown> & {
	vert?: string;
	tab?: string;
	los?: number | string;
	f?: string;
	pt?: string;
};

// Called on the front-tab passthru redirect right before window.open. Must
// never delay or break the redirect: every failure path returns formData
// unchanged (fail-open to the regular Kayak flow, without an exposure).
export const applyLastMinuteHotelExperiment = (
	formData: PassthruFormData,
	{ countryCode }: { countryCode?: string | null }
): PassthruFormData => {
	try {
		if (!formData || formData.vert !== 'hotels' || formData.tab !== 'front') {
			return formData;
		}

		const signals: LastMinuteHotelSignals = {
			countryCode,
			// Google Ads = the gclid seen on the LANDING request (snapshotted once in
			// init(); mirrors the backend's query:"gclid" -> GoogleTrafficSource).
			// NOT re-read from the live URL here — that drops the param on SPA nav
			// (under-inclusion) and accepts stray gclids (over-inclusion).
			hasGclid: hasLandingGoogleAdsClick(),
			los: formData.los,
			f: firstValue(formData.f),
		};

		// Submitted before /init resolved (country unknown): fail-open, but keep
		// the fastest searchers visible to monitoring — this blind spot is exactly
		// the fail-open selection bias PRD stories 29/30 want measured.
		if (!countryCode) {
			if (passesNonGeoCriteria(signals)) {
				emitGAEvent('experiment_eligible', 'hotels', {
					feature_key: LAST_MINUTE_HOTEL_FEATURE_KEY,
					gb_ready: false,
					reason: 'init_pending',
				});
			}
			return formData;
		}

		if (!isLastMinuteHotelEligible(signals)) return formData;

		const gbReady = isGrowthbookReady(LAST_MINUTE_HOTEL_FEATURE_KEY);

		// Eligible-volume + "flag not ready at submit" monitoring: hard criteria
		// can starve the sample, and fail-open drops the fastest searchers — both
		// need to be observable (PRD stories 29/30).
		emitGAEvent('experiment_eligible', 'hotels', {
			feature_key: LAST_MINUTE_HOTEL_FEATURE_KEY,
			gb_ready: gbReady,
			...(gbReady ? {} : { reason: 'features_pending' }),
		});

		if (!gbReady) return formData;

		// Bucketing point for both arms: the exposure is logged inside this call.
		const isTreatment = isFeatureOn(LAST_MINUTE_HOTEL_FEATURE_KEY);

		if (!isTreatment) return formData;

		// Treatment: swap the front partner to the run's CPA partner. Attribution
		// params stay intact. Per the ticket, a pt=2 on the URL is IGNORED for this
		// front-tab redirect (dropped so the standard CPA flow applies); any
		// other param is never touched. Control keeps everything as-is.
		const treated: PassthruFormData = { ...formData, f: TREATMENT_FRONT_PARTNER };
		if (firstValue(treated.pt) === IGNORED_PT) delete treated.pt;
		return treated;
	} catch (e) {
		console.log('last-minute-hotel: experiment failed open to default redirect', e);
		return formData;
	}
};
